import { EAgeGroup, EWorkshopLanguage } from 'data/types/generic.types';
import {
  TWorkshopEventCardDB,
  TWorkshopEventCardUI,
  TWorkshopEventSessionUI,
  TWorkshopEventsResponseDB,
  TWorkshopEventsResponseUI,
} from 'data/types/workshopApp.types';

const findNextSession = (
  classes: TWorkshopEventCardDB['classes'],
): TWorkshopEventSessionUI | null => {
  const futureSessions = classes
    .flatMap(cls => cls.sessions)
    .filter(s => !s.isPassed)
    .sort(
      (a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime(),
    );
  return futureSessions[0] || null;
};

const extractAgeGroups = (classes: TWorkshopEventCardDB['classes']) => {
  const groups = new Set<EAgeGroup>();
  classes.forEach(cls => groups.add(cls.ageGroup));
  return Array.from(groups);
};

const extractLanguages = (classes: TWorkshopEventCardDB['classes']) => {
  const langs = new Set<EWorkshopLanguage>();
  classes.forEach(cls => langs.add(cls.language));
  return Array.from(langs);
};

export const workshopEventCardConverter = {
  fromDb(card: TWorkshopEventCardDB): TWorkshopEventCardUI {
    const languages = extractLanguages(card.classes);
    return {
      workshopId: card.workshopId,
      hostId: card.hostId,
      creationDate: card.creationDate,
      workshopType: card.workshopType,
      attendanceType: card.attendanceType,
      workshopCategory: card.workshopCategory,
      workshopPhoto: card.workshopPhoto,
      childcareProvided: card.childcareProvided,
      foodProvided: card.foodProvided,
      locationName: card.locationName?.value || null,
      locationAddress: card.locationAddress?.value || null,
      classes: card.classes.map(cls => ({
        workshopClassId: cls.workshopClassId,
        ageGroup: cls.ageGroup,
        language: cls.language,
        sessions: cls.sessions,
      })),
      ageGroups: extractAgeGroups(card.classes),
      languages,
      hasSpanish: languages.includes(EWorkshopLanguage.SPANISH),
      nextSession: findNextSession(card.classes),
    };
  },
};

export const workshopEventsResponseConverter = {
  fromDb(response: TWorkshopEventsResponseDB): TWorkshopEventsResponseUI {
    return {
      hostName: response.hostName?.value || null,
      workshopEvents: response.workshopEvents.map(card =>
        workshopEventCardConverter.fromDb(card),
      ),
    };
  },
};
